import { useState } from "react";
import { useUser } from "@clerk/nextjs";
import type { Scopes } from "@khol/google/apis";
import { useNavigation } from "../createUseNavigation/useNavigation";

/**
 * @deprecated use package "@khol/react/hooks" instead
 */
export const useClerkUser = () => {
  const { user } = useUser();
  const { push } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const isScopeApproved = (
    scopesToAdd: addScopesExternalAccountParams["scopes"],
    approvedScopes: string
  ) => scopesToAdd.every((scope) => approvedScopes.includes(scope));

  const addScopesExternalAccount = async ({
    scopes,
    provider,
  }: addScopesExternalAccountParams) => {
    setIsLoading(true);
    setError("");

    const externalAccount = user?.externalAccounts.find(
      (ea) => ea.provider === provider
    );
    if (!externalAccount) {
      setError("No external account found.");
      setIsLoading(false);
      return;
    }
    if (isScopeApproved(scopes, externalAccount.approvedScopes)) {
      setError("Already authorized for these scopes.");
      setIsLoading(false);
      return;
    }

    try {
      const res = await externalAccount.reauthorize({
        additionalScopes: scopes,
        redirectUrl: "/", // Consider using a more dynamic redirect
      });

      if (!res.verification?.externalVerificationRedirectURL) {
        setError("Authorization redirection URL not found.");
      } else {
        push(res.verification.externalVerificationRedirectURL.href);
      }
    } catch (error) {
      setError("An error occurred during authorization.");
      console.error("Authorization error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return { user, addScopesExternalAccount, isLoading, error };
};

type addScopesExternalAccountParams = {
  scopes: Scopes[];
  provider: OAuthProvider;
};

type OAuthProvider =
  | "facebook"
  | "google"
  | "hubspot"
  | "github"
  | "tiktok"
  | "gitlab"
  | "discord"
  | "twitter"
  | "twitch"
  | "linkedin"
  | "linkedin_oidc"
  | "dropbox"
  | "bitbucket"
  | "microsoft"
  | "notion"
  | "apple"
  | "x";
